import { useMemo, useRef } from 'react';

import { sendInternalFEEvent } from '../eventUtil';
import { openNewTab } from '../utils/open-new-tab';
import { InternalMessageEventsName } from '../constants/enums';
import { getQueryParam } from '../utils/get-query-param';

export function useTabManager() {
  const windowRef = useRef<Window | null>(null);
  const pageLoadPromiseRef = useRef<Promise<boolean> | null>();

  const openTab = (url: string) => {
    resetTabState();
    const tab = openNewTab(url);

    windowRef.current = tab;
    pageLoadPromiseRef.current = new Promise((resolve, reject) => {
      if (!tab) {
        reject(new Error('Failed to open the new tab.'));
        return;
      }

      tab.onload = function () {
        resolve(true);
      };

      tab.onerror = function () {
        reject(new Error('Error occurred while loading the new tab.'));
      };
    });
  };

  const redirectToUrl = (redirectUrl?: string) => {
    sendInternalFEEvent(windowRef.current, InternalMessageEventsName.APM_REDIRECT, redirectUrl);
  };

  const isTabClosed = () => {
    return Boolean(windowRef.current?.closed);
  };

  const isTabLoaded = () => {
    return pageLoadPromiseRef.current;
  };

  const getQueryParams = (queryParamName: string) => {
    try {
      if (windowRef.current) {
        return getQueryParam(queryParamName, windowRef.current);
      }
      return '';
    } catch (error) {
      console.error('Error getting query params', error);
      return '';
    }
  };

  const closeTab = (): void => {
    sendInternalFEEvent(windowRef.current, InternalMessageEventsName.APM_REDIRECT_CLOSE);
    resetTabState();
  };

  const resetTabState = () => {
    windowRef.current = null;
    pageLoadPromiseRef.current = null;
  };

  return useMemo(
    () => ({
      tabManager: {
        openTab,
        closeTab,
        isTabClosed,
        isTabLoaded,
        redirectToUrl,
        getQueryParams,
      },
    }),
    [],
  );
}
